import React, { useContext, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import RefreshIcon from "@material-ui/icons/Refresh";
import SaveIcon from "@material-ui/icons/Save";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import CloseIcon from "@material-ui/icons/Close";

import { PageContext } from "../../../contexts/PageContext";
import { UserContext } from "../../../contexts/UserContext";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
  },
  row: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  pinField: {
    width: 160,
  },
  actionButton: {
    marginLeft: theme.spacing(1),
  },
}));

export default function CustomerPortalPinDialog({ creditId, portals = [], onChanged }) {
  const classes = useStyles();
  const pageContext = useContext(PageContext);
  const userContext = useContext(UserContext);

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [pinsByPortalId, setPinsByPortalId] = useState({});
  const [visibleByPortalId, setVisibleByPortalId] = useState({});

  const availablePortals = useMemo(() => portals.filter((p) => p && p.id), [portals]);

  const loadPins = () => {
    if (!availablePortals.length) {
      return;
    }

    setIsLoading(true);
    Promise.all(
      availablePortals.map((portal) => {
        return axios({
          url: `${process.env.REACT_APP_API_SERVICE_URL}/portals/${creditId}/${portal.id}/pin`,
          method: "get",
          headers: userContext.getRequestAuthHeader(),
        }).then((res) => ({ portalId: portal.id, pin: res.data.portal_pin || "" }));
      })
    )
      .then((results) => {
        const pinMap = {};
        results.forEach((item) => {
          pinMap[item.portalId] = item.pin;
        });
        setPinsByPortalId(pinMap);
      })
      .catch((err) => {
        console.log(err);
        pageContext.setErrorMessage("Kundenportal PINs konnten nicht geladen werden.");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleOpen = () => {
    setOpen(true);
    loadPins();
  };

  const savePin = (portalId) => {
    const pin = pinsByPortalId[portalId] || "";
    if (!/^\d{4}$/.test(pin)) {
      pageContext.setErrorMessage("PIN muss aus genau 4 Ziffern bestehen.");
      return;
    }

    axios({
      url: `${process.env.REACT_APP_API_SERVICE_URL}/portals/${creditId}/${portalId}/pin`,
      method: "put",
      data: { portal_pin: pin },
      headers: userContext.getRequestAuthHeader(),
    })
      .then((res) => {
        setPinsByPortalId((prev) => ({ ...prev, [portalId]: res.data.portal_pin || pin }));
        pageContext.setInfoMessage("PIN wurde gespeichert.");
      })
      .catch((err) => {
        console.log(err);
        pageContext.setErrorMessage("PIN konnte nicht gespeichert werden.");
      });
  };

  const resetPin = (portalId) => {
    axios({
      url: `${process.env.REACT_APP_API_SERVICE_URL}/portals/${creditId}/${portalId}/pin`,
      method: "put",
      data: { reset: true },
      headers: userContext.getRequestAuthHeader(),
    })
      .then((res) => {
        setPinsByPortalId((prev) => ({ ...prev, [portalId]: res.data.portal_pin || "" }));
        pageContext.setInfoMessage("PIN wurde zurückgesetzt.");
      })
      .catch((err) => {
        console.log(err);
        pageContext.setErrorMessage("PIN konnte nicht zurückgesetzt werden.");
      });
  };

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<VpnKeyIcon />}
        onClick={handleOpen}
      >
        Kundenportal PIN
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>
          Kundenportal PIN
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => setOpen(false)}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {!availablePortals.length && (
            <Typography variant="body2">Es gibt noch kein Kundenportal für diese Finanzierung.</Typography>
          )}
          {availablePortals.map((portal) => {
            const pinValue = pinsByPortalId[portal.id] || "";
            const isVisible = !!visibleByPortalId[portal.id];
            return (
              <Grid container spacing={1} alignItems="center" key={portal.id} className={classes.row}>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2">Kreditnehmer {portal.person_index}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    className={classes.pinField}
                    label="PIN"
                    value={pinValue}
                    type={isVisible ? "text" : "password"}
                    inputProps={{ maxLength: 4 }}
                    onChange={(e) => {
                      const nextPin = (e.target.value || "").replace(/\D/g, "").slice(0, 4);
                      setPinsByPortalId((prev) => ({ ...prev, [portal.id]: nextPin }));
                    }}
                    disabled={isLoading}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={isVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    onClick={() => {
                      setVisibleByPortalId((prev) => ({ ...prev, [portal.id]: !isVisible }));
                    }}
                  >
                    {isVisible ? "ausblenden" : "anzeigen"}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    className={classes.actionButton}
                    startIcon={<RefreshIcon />}
                    onClick={() => resetPin(portal.id)}
                    disabled={isLoading}
                  >
                    zurücksetzen
                  </Button>
                  <Button
                    size="small"
                    color="primary"
                    variant="contained"
                    className={classes.actionButton}
                    startIcon={<SaveIcon />}
                    onClick={() => savePin(portal.id)}
                    disabled={isLoading}
                  >
                    speichern
                  </Button>
                </Grid>
              </Grid>
            );
          })}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false);
              if (onChanged) {
                onChanged();
              }
            }}
            color="primary"
            variant="contained"
          >
            schließen
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
